import React, { useEffect, useRef } from 'react';
import { useGameStore } from '../../state/game-store.js';
import { GAME_CONSTANTS } from '@tankworld/shared';
import { Shield, Crosshair, Compass, Gauge, Radio, Eye } from 'lucide-react';

export const InGameHUD: React.FC = () => {
  const speedKmh = useGameStore((s) => s.speedKmh);
  const health = useGameStore((s) => s.health);
  const maxHealth = useGameStore((s) => s.maxHealth);
  const isAlive = useGameStore((s) => s.isAlive);
  const reloadProgress = useGameStore((s) => s.reloadProgress);
  const hullHeadingDeg = useGameStore((s) => s.hullHeadingDeg);
  const turretHeadingDeg = useGameStore((s) => s.turretHeadingDeg);
  const gunElevationDeg = useGameStore((s) => s.gunElevationDeg);
  const kills = useGameStore((s) => s.kills);
  const deaths = useGameStore((s) => s.deaths);
  const score = useGameStore((s) => s.score);
  const fps = useGameStore((s) => s.fps);
  const pingMs = useGameStore((s) => s.pingMs);
  const serverTick = useGameStore((s) => s.serverTick);
  const cameraMode = useGameStore((s) => s.cameraMode);
  const gameMode = useGameStore((s) => s.gameMode);
  const activeRoom = useGameStore((s) => s.activeRoom);
  const combatEvents = useGameStore((s) => s.combatEvents);
  const tanks = useGameStore((s) => s.tanks);
  const localPlayerId = useGameStore((s) => s.localPlayerId);

  // Minimap canvas
  const minimapRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = minimapRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Radar background grid
    ctx.fillStyle = 'rgba(15, 20, 18, 0.85)';
    ctx.fillRect(0, 0, w, h);

    // Range rings
    ctx.strokeStyle = 'rgba(90, 120, 80, 0.35)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(w / 2, h / 2, w * 0.22, 0, Math.PI * 2);
    ctx.arc(w / 2, h / 2, w * 0.44, 0, Math.PI * 2);
    ctx.stroke();

    // Crosshairs
    ctx.beginPath();
    ctx.moveTo(w / 2, 0); ctx.lineTo(w / 2, h);
    ctx.moveTo(0, h / 2); ctx.lineTo(w, h / 2);
    ctx.stroke();

    // World scale mapping (400m world -> w px)
    const mapScale = (w * 0.85) / GAME_CONSTANTS.ARENA_SIZE;
    const localTank = tanks.find((t) => t.id === localPlayerId);

    // Draw all tanks on radar
    for (const t of tanks) {
      if (!t.isAlive) continue;
      const isSelf = t.id === localPlayerId;

      // Position relative to center or world
      const mx = w / 2 + t.position.x * mapScale;
      const my = h / 2 + t.position.z * mapScale;

      ctx.save();
      ctx.translate(mx, my);
      ctx.rotate(t.hullYaw);

      if (isSelf) {
        // Player tank icon: green triangle with turret direction line
        ctx.fillStyle = '#4ade80';
        ctx.beginPath();
        ctx.moveTo(0, -6);
        ctx.lineTo(4, 5);
        ctx.lineTo(-4, 5);
        ctx.closePath();
        ctx.fill();

        // Turret line
        ctx.rotate(t.turretYaw - t.hullYaw);
        ctx.strokeStyle = '#22c55e';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(0, -12);
        ctx.stroke();
      } else {
        // Enemy / Bot icon: red diamond
        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.moveTo(0, -4);
        ctx.lineTo(4, 0);
        ctx.lineTo(0, 4);
        ctx.lineTo(-4, 0);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    }
  }, [tanks, localPlayerId]);

  const isReloading = reloadProgress < 1.0;
  const reloadPct = Math.round(reloadProgress * 100);

  return (
    <div className="absolute inset-0 pointer-events-none flex flex-col justify-between p-4 font-mono text-zinc-200">
      {/* 1. SNIPER SCOPE OVERLAY */}
      {cameraMode === 'sniper' && (
        <div className="absolute inset-0 sniper-scope flex items-center justify-center">
          {/* Mil-dot crosshair lines */}
          <div className="relative w-96 h-96 border border-emerald-500/40 rounded-full flex items-center justify-center">
            <div className="absolute w-full h-[1px] bg-emerald-500/60" />
            <div className="absolute h-full w-[1px] bg-emerald-500/60" />

            {/* Ballistic elevation notches */}
            {[-40, -20, 20, 40, 60].map((y) => (
              <div
                key={y}
                className="absolute w-6 h-[1px] bg-emerald-400/80"
                style={{ transform: `translateY(${y}px)` }}
              />
            ))}

            <div className="absolute top-4 text-xs text-emerald-400 tracking-wider">
              125MM APFSDS // OPTIC 8X
            </div>
            <div className="absolute bottom-4 text-xs text-emerald-400">
              ELEV: {gunElevationDeg > 0 ? `+${gunElevationDeg}` : gunElevationDeg}°
            </div>
          </div>
        </div>
      )}

      {/* 2. TOP TELEMETRY BAR */}
      <div className="flex flex-wrap justify-between items-start gap-2 md:gap-4">
        {/* Match and Mode Status */}
        <div className="hud-panel px-2.5 py-1.5 md:px-4 md:py-2 rounded border border-emerald-900/50 flex items-center gap-2.5 md:gap-6">
          <div className="flex items-center gap-1.5 md:gap-2">
            <Radio className="w-3.5 h-3.5 md:w-4 md:h-4 text-emerald-400 animate-pulse" />
            <span className="text-emerald-400 font-bold uppercase tracking-wider text-xs md:text-sm">
              {gameMode === 'singleplayer' ? 'PROVING GROUNDS' : activeRoom?.name || 'MULTIPLAYER'}
            </span>
          </div>

          <div className="flex items-center gap-2 md:gap-4 text-[10px] md:text-xs text-zinc-400">
            <span>K: <strong className="text-emerald-400">{kills}</strong></span>
            <span>D: <strong className="text-rose-400">{deaths}</strong></span>
            <span>PTS: <strong className="text-amber-400">{score}</strong></span>
          </div>
        </div>

        {/* Diagnostics panel (Tablet / Desktop) */}
        <div className="hud-panel hidden sm:flex px-2.5 py-1 rounded border border-emerald-900/40 items-center gap-3 text-xs text-zinc-400">
          <span>FPS: <strong className="text-emerald-400">{fps}</strong></span>
          {gameMode === 'multiplayer' && (
            <span>PING: <strong className={pingMs < 80 ? 'text-emerald-400' : 'text-amber-400'}>{pingMs}ms</strong></span>
          )}
          <span>TICK: <strong className="text-zinc-300">#{serverTick}</strong></span>
        </div>

        {/* Combat feed (Desktop / Large screen) */}
        <div className="hud-panel hidden md:block w-72 p-2.5 rounded border border-emerald-900/40 space-y-1 text-xs">
          <div className="text-[10px] text-zinc-400 uppercase tracking-wider pb-1 border-b border-zinc-800">
            Tactical Combat Feed
          </div>
          {combatEvents.slice(0, 4).map((evt) => (
            <div key={evt.id} className="text-xs truncate">
              {evt.type === 'kill' ? (
                <span className="text-rose-400 font-bold">
                  {evt.attackerName} ELIMINATED {evt.victimName}
                </span>
              ) : evt.type === 'hit' ? (
                <span className="text-amber-300">
                  {evt.attackerName} hit {evt.victimName} (-{evt.damage})
                </span>
              ) : (
                <span className="text-zinc-400">{evt.attackerName} fired main gun</span>
              )}
            </div>
          ))}
          {combatEvents.length === 0 && (
            <div className="text-zinc-500 italic text-[11px]">No active contact</div>
          )}
        </div>
      </div>


      {/* 3. CENTER SCREEN RETICLE (When not in sniper mode) */}
      {cameraMode !== 'sniper' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative flex items-center justify-center">
            {/* Center crosshair */}
            <div className="w-8 h-8 border border-emerald-500/40 rounded-full flex items-center justify-center">
              <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
            </div>

            {/* Reload circular progress gauge */}
            <svg className="absolute w-16 h-16 -rotate-90">
              <circle
                cx="32"
                cy="32"
                r="26"
                stroke="rgba(60, 80, 60, 0.4)"
                strokeWidth="2.5"
                fill="none"
              />
              <circle
                cx="32"
                cy="32"
                r="26"
                stroke={isReloading ? '#f59e0b' : '#4ade80'}
                strokeWidth="2.5"
                fill="none"
                strokeDasharray="163.3"
                strokeDashoffset={163.3 * (1 - reloadProgress)}
                className="transition-all duration-75"
              />
            </svg>

            {/* Status under reticle */}
            <div className="absolute top-10 text-[10px] tracking-wider text-emerald-400 font-bold">
              {isReloading ? `RELOADING ${reloadPct}%` : 'READY TO FIRE'}
            </div>
          </div>
        </div>
      )}

      {/* 4. BOTTOM TELEMETRY & RADAR */}
      <div className="flex justify-between items-end gap-3 md:gap-6">
        {/* Left: Speedometer & Health & Heading */}
        <div className="hud-panel p-2.5 sm:p-3 md:p-4 rounded-lg border border-emerald-900/50 space-y-2 md:space-y-3 min-w-[170px] sm:min-w-[220px] md:min-w-[280px] mb-36 sm:mb-40 md:mb-0">
          {/* Health Bar */}
          <div>
            <div className="flex justify-between text-[11px] md:text-xs mb-1">
              <span className="flex items-center gap-1.5 text-zinc-300 font-bold">
                <Shield className="w-3.5 h-3.5 text-emerald-400" /> HULL
              </span>
              <span className={`font-bold ${health > 35 ? 'text-emerald-400' : 'text-rose-500'}`}>
                {health} / {maxHealth} HP
              </span>
            </div>
            <div className="w-full h-2.5 md:h-3 bg-zinc-900 rounded overflow-hidden border border-zinc-700">
              <div
                className={`h-full transition-all duration-200 ${
                  health > 50 ? 'bg-emerald-500' : health > 25 ? 'bg-amber-500' : 'bg-rose-600 animate-pulse'
                }`}
                style={{ width: `${(health / maxHealth) * 100}%` }}
              />
            </div>
          </div>

          {/* Speedometer & Gear */}
          <div className="grid grid-cols-2 gap-2 md:gap-3 pt-1.5 md:pt-2 border-t border-zinc-800 text-xs">
            <div className="flex items-center gap-1.5 md:gap-2">
              <Gauge className="w-3.5 h-3.5 md:w-4 md:h-4 text-emerald-400" />
              <div>
                <div className="text-[9px] md:text-[10px] text-zinc-400 uppercase">Speed</div>
                <div className="text-sm md:text-lg font-bold text-zinc-100">{speedKmh} <span className="text-[9px] md:text-xs font-normal text-zinc-400">KM/H</span></div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 md:gap-2">
              <Compass className="w-3.5 h-3.5 md:w-4 md:h-4 text-emerald-400" />
              <div>
                <div className="text-[9px] md:text-[10px] text-zinc-400 uppercase">Azimuth</div>
                <div className="text-sm md:text-lg font-bold text-zinc-100">{hullHeadingDeg}° <span className="text-[9px] md:text-xs font-normal text-zinc-400">HULL</span></div>
              </div>
            </div>
          </div>

          {/* Camera View Switch Indicator */}
          <div className="hidden sm:flex pt-1.5 md:pt-2 border-t border-zinc-800 items-center justify-between text-[10px] md:text-[11px] text-zinc-400">
            <span className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5 text-emerald-400" /> CAM: <strong className="text-emerald-400 uppercase">{cameraMode.replace('_', ' ')}</strong>
            </span>
            <span className="text-zinc-500 hidden md:inline">[C] Cycle View</span>
          </div>
        </div>

        {/* Center: Controls reminder (Desktop only) */}
        <div className="hud-panel hidden lg:block px-4 py-2 rounded text-xs text-zinc-400 border border-emerald-900/30 text-center">
          <div><strong className="text-zinc-200">W/S</strong>: Throttle & Reverse &bull; <strong className="text-zinc-200">A/D</strong>: Steer &bull; <strong className="text-zinc-200">LMB / Space</strong>: Fire</div>
          <div className="text-[10px] text-zinc-500 mt-0.5"><strong className="text-zinc-300">C</strong>: Change Camera &bull; <strong className="text-zinc-300">Esc</strong>: Tactical Menu</div>
        </div>

        {/* Right: Radar Minimap */}
        <div className="hud-panel p-1.5 sm:p-2 md:p-2.5 rounded-lg border border-emerald-900/50 flex flex-col items-center mb-36 sm:mb-40 md:mb-0">
          <div className="text-[9px] md:text-[10px] text-emerald-400 uppercase font-bold tracking-wider mb-1 flex items-center gap-1">
            <Crosshair className="w-3 h-3" /> Radar
          </div>
          <canvas
            ref={minimapRef}
            width={160}
            height={160}
            className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded border border-emerald-950/60"
          />
        </div>
      </div>
    </div>
  );
};
